<template>
	<div id="laborSettlement">
		<!-- ① 顶部操作栏 -->
		<div class="page-header">
			<div class="header-left">
				<div class="back-btn" @click="$router.back()">
					<i class="el-icon-arrow-left"></i>
				</div>
				<h1>{{ collectData.meetingName || '劳务结算' }}</h1>
			</div>
			<div class="header-actions">
				<el-button size="small" @click="save">保存</el-button>
				<el-button size="small" type="primary" @click="submitForReview"
					:disabled="collectData.progress < 100">
					提交审核
				</el-button>
			</div>
		</div>

		<!-- ② 收集进度条 -->
		<div class="progress-bar" v-if="collectData.totalItems > 0">
			<div class="progress-info">
				<span class="progress-label">📊 材料收集进度</span>
				<span class="progress-nums">
					<b :class="collectData.progress >= 100 ? 'done' : ''">{{ collectData.collectedItems }}</b> / {{ collectData.totalItems }}
				</span>
			</div>
			<el-progress :percentage="collectData.progress" :stroke-width="8"
				:color="collectData.progress >= 100 ? '#059669' : '#667eea'"
				:show-text="false" />
			<div class="progress-hint">
				<template v-if="collectData.progress >= 100">
					<i class="el-icon-success"></i> 全部材料已收集，可提交审核
				</template>
				<template v-else>
					<i class="el-icon-warning"></i> 还有 {{ collectData.totalItems - collectData.collectedItems }} 项待补充
				</template>
			</div>
		</div>

		<div class="content" v-loading="loading">
			<!-- ③ 统计卡片 -->
			<div class="stat-cards">
				<div class="stat-card">
					<div class="stat-label">参会医生</div>
					<div class="stat-value">{{ collectData.doctorCount || 0 }}</div>
					<div class="stat-sub">
						{{ completeDoctorCount }} 人已完成 / {{ incompleteDoctorCount }} 人待补充
					</div>
				</div>
				<div class="stat-card">
					<div class="stat-label">劳务总额</div>
					<div class="stat-value fee">¥{{ formatMoney(collectData.totalLaborFee) }}</div>
					<div class="stat-sub">税前金额</div>
				</div>
				<div class="stat-card">
					<div class="stat-label">劳务明细表</div>
					<div class="stat-value small">自动生成</div>
					<div class="stat-sub">
						<el-button size="mini" type="text" icon="el-icon-download">下载 Excel</el-button>
					</div>
				</div>
			</div>

			<!-- ④ 合规文件 -->
			<div class="section">
				<h2 class="section-title">合规文件</h2>
				<div class="compliance-grid">
					<div class="compliance-item" v-for="f in collectData.complianceFiles" :key="f.key"
						:class="{ collected: f.collected }">
						<div class="compliance-icon">
							<i :class="f.icon"></i>
						</div>
						<div class="compliance-info">
							<div class="compliance-name">
								{{ f.label }}
								<el-tag v-if="f.autoGenerated" size="mini" type="success">自动</el-tag>
							</div>
							<div class="compliance-status" v-if="f.collected">
								<i class="el-icon-success"></i> {{ f.fileCount }} 个文件
							</div>
							<div class="compliance-status missing" v-else>
								<i class="el-icon-plus"></i> 点击上传
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- ⑤ 医生文件夹 -->
			<div class="section">
				<h2 class="section-title">医生文件夹</h2>

				<!-- 需补充 -->
				<template v-if="incompleteDoctors.length > 0">
					<div class="sub-title warn">⚠️ 需补充 ({{ incompleteDoctors.length }}人)</div>
					<div class="doctor-card" v-for="doc in incompleteDoctors" :key="doc.participantId">
						<div class="doctor-info">
							<div class="avatar">{{ (doc.doctorName || '?').charAt(0) }}</div>
							<div class="info-text">
								<div class="name-line">
									<b>{{ doc.doctorName }}</b>
									<span class="phone">{{ doc.doctorPhone }}</span>
									<el-tag size="mini" type="info">{{ roleLabel(doc.role) }}</el-tag>
								</div>
								<div class="fee-line">
									劳务费: <b class="fee">¥{{ formatMoney(doc.laborFee) }}</b>
									<span v-if="doc.hospital" class="hospital">{{ doc.hospital }}</span>
								</div>
							</div>
						</div>
						<div class="file-checklist">
							<div class="file-item" v-for="(f, key) in doc.files" :key="key"
								:class="{ done: f.collected, required: f.required && !f.collected }">
								<i :class="f.collected ? 'el-icon-success' : (f.required ? 'el-icon-warning' : 'el-icon-plus')"></i>
								{{ f.label }}
								<span class="source-tag" v-if="f.collected && f.source !== 'manual'">{{ sourceLabel(f.source) }}</span>
							</div>
						</div>
						<div class="doctor-actions">
							<el-button size="mini" type="primary" icon="el-icon-upload2"
								@click="openUpload(doc)">补充文件</el-button>
						</div>
					</div>
				</template>

				<!-- 已完成 -->
				<template v-if="completeDoctors.length > 0">
					<div class="sub-title success" @click="showComplete = !showComplete" style="cursor:pointer">
						✅ 已完成 ({{ completeDoctors.length }}人)
						<i :class="showComplete ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" style="margin-left:6px"></i>
					</div>
					<template v-if="showComplete">
						<div class="doctor-card compact" v-for="doc in completeDoctors" :key="doc.participantId">
							<div class="doctor-info">
								<div class="avatar small">{{ (doc.doctorName || '?').charAt(0) }}</div>
								<div class="info-text">
									<b>{{ doc.doctorName }}</b>
									<span class="phone">{{ doc.doctorPhone }}</span>
									<b class="fee" style="margin-left:12px">¥{{ formatMoney(doc.laborFee) }}</b>
								</div>
								<div class="done-badge"><i class="el-icon-success"></i> 全部完成</div>
							</div>
						</div>
					</template>
				</template>

				<!-- 空状态 -->
				<div class="empty-state" v-if="!loading && collectData.doctorCount === 0">
					<i class="el-icon-user"></i>
					<p>暂无参会医生数据</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';

export default {
	name: 'LaborSettlement',
	data() {
		return {
			loading: false,
			showComplete: false,
			meetingId: null,
			collectData: {
				meetingName: '',
				doctorCount: 0,
				totalLaborFee: 0,
				totalItems: 0,
				collectedItems: 0,
				progress: 0,
				doctors: [],
				complianceFiles: [],
			}
		}
	},
	computed: {
		completeDoctors() {
			return (this.collectData.doctors || []).filter(d => d.complete);
		},
		incompleteDoctors() {
			return (this.collectData.doctors || []).filter(d => !d.complete);
		},
		completeDoctorCount() {
			return this.completeDoctors.length;
		},
		incompleteDoctorCount() {
			return this.incompleteDoctors.length;
		}
	},
	created() {
		this.meetingId = this.$route.query.meetingId || this.$route.query.mId;
		if (this.meetingId) {
			this.loadCollect();
		}
	},
	methods: {
		loadCollect() {
			this.loading = true;
			const token = JSON.parse(localStorage.getItem('token'));
			axios.get('/api/spending/labor/collect', {
				params: { meetingId: this.meetingId },
				headers: { token: token?.token }
			}).then(res => {
				if (res.data?.code === 200) {
					this.collectData = res.data.data;
				} else {
					this.$message.error(res.data?.msg || '加载失败');
				}
				this.loading = false;
			}).catch(() => { this.loading = false; });
		},

		openUpload(doc) {
			this.$message.info(`打开 ${doc.doctorName} 补充文件弹窗（待实现）`);
		},

		save() {
			this.$message.success('已保存');
		},

		submitForReview() {
			this.$confirm('确认提交审核？提交后不可修改。', '提交审核', {
				type: 'warning'
			}).then(() => {
				this.$message.success('已提交审核');
			}).catch(() => {});
		},

		roleLabel(r) {
			return { SPEAKER: '讲者', CHAIR: '主席', ATTENDEE: '参会者', HOST: '主持人' }[r] || r || '';
		},
		sourceLabel(s) {
			return { fadada: '电子签', meeting_participant: '自动', auto_screenshot: '自动截图', auto_generate: '自动生成' }[s] || '';
		},
		formatMoney(v) {
			if (!v) return '0';
			return Number(v).toLocaleString();
		}
	}
}
</script>

<style lang="scss" scoped>
#laborSettlement {
	min-height: 100%;
	padding: 28px 36px;
	background: #f0f2f8;

	.page-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 20px;

		.header-left {
			display: flex;
			align-items: center;
			gap: 14px;
		}

		.back-btn {
			width: 34px; height: 34px; border-radius: 10px;
			background: #fff; display: flex; align-items: center; justify-content: center;
			cursor: pointer; transition: all 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
			&:hover { background: #667eea; color: #fff; }
			i { font-size: 16px; font-weight: 700; }
		}

		h1 { font-size: 20px; font-weight: 700; color: #1a1a2e; margin: 0; }
	}

	// ② 进度条
	.progress-bar {
		background: #fff;
		border-radius: 14px;
		padding: 16px 24px;
		margin-bottom: 20px;
		box-shadow: 0 1px 4px rgba(0,0,0,0.04);

		.progress-info {
			display: flex; justify-content: space-between; align-items: center;
			margin-bottom: 8px;
			.progress-label { font-size: 14px; font-weight: 600; color: #1a1a2e; }
			.progress-nums { font-size: 13px; color: #8f9bb3;
				b { color: #667eea; &.done { color: #059669; } }
			}
		}

		.progress-hint {
			margin-top: 8px; font-size: 12px; color: #8f9bb3;
			.el-icon-success { color: #059669; }
			.el-icon-warning { color: #e6a23c; }
		}
	}

	// ③ 统计卡片
	.stat-cards {
		display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 20px;

		.stat-card {
			background: #fff; border-radius: 14px; padding: 18px 22px;
			box-shadow: 0 1px 4px rgba(0,0,0,0.04);

			.stat-label { font-size: 12px; color: #8f9bb3; margin-bottom: 6px; }
			.stat-value { font-size: 28px; font-weight: 700; color: #1a1a2e;
				&.fee { color: #e6a23c; }
				&.small { font-size: 18px; }
			}
			.stat-sub { font-size: 12px; color: #a0aec0; margin-top: 4px; }
		}
	}

	// 通用 section
	.section {
		background: #fff; border-radius: 14px; padding: 22px 24px;
		margin-bottom: 20px; box-shadow: 0 1px 4px rgba(0,0,0,0.04);
	}

	.section-title {
		font-size: 16px; font-weight: 700; color: #1a1a2e; margin: 0 0 16px;
	}

	// ④ 合规文件
	.compliance-grid {
		display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;

		.compliance-item {
			display: flex; align-items: center; gap: 12px;
			padding: 14px 16px; border-radius: 10px;
			background: #f8f9fc; border: 1px solid #eef0f6;
			cursor: pointer; transition: all 0.2s;

			&:hover { border-color: #667eea; }
			&.collected { background: rgba(5, 150, 105, 0.04); border-color: rgba(5, 150, 105, 0.15); }

			.compliance-icon {
				width: 38px; height: 38px; border-radius: 10px;
				background: rgba(102, 126, 234, 0.08);
				display: flex; align-items: center; justify-content: center;
				i { font-size: 18px; color: #667eea; }
			}

			&.collected .compliance-icon {
				background: rgba(5, 150, 105, 0.08);
				i { color: #059669; }
			}

			.compliance-name { font-size: 13px; font-weight: 600; color: #1a1a2e; }
			.compliance-status {
				font-size: 11px; color: #059669; margin-top: 2px;
				&.missing { color: #a0aec0; }
			}
		}
	}

	// ⑤ 医生文件夹
	.sub-title {
		font-size: 13px; font-weight: 700; padding: 8px 0; margin-bottom: 8px;
		&.warn { color: #e6a23c; }
		&.success { color: #059669; }
	}

	.doctor-card {
		background: #fafbfe; border-radius: 12px; padding: 16px 20px;
		margin-bottom: 8px; border: 1px solid #eef0f6; transition: all 0.2s;

		&:hover { border-color: #667eea; box-shadow: 0 2px 8px rgba(102,126,234,0.08); }

		&.compact { padding: 12px 20px; }

		.doctor-info {
			display: flex; align-items: center; gap: 12px; margin-bottom: 10px;

			.avatar {
				width: 40px; height: 40px; border-radius: 10px;
				background: linear-gradient(135deg, #667eea, #764ba2);
				color: #fff; display: flex; align-items: center; justify-content: center;
				font-size: 16px; font-weight: 700; flex-shrink: 0;
				&.small { width: 32px; height: 32px; font-size: 13px; border-radius: 8px; }
			}

			.info-text { flex: 1;
				.name-line { display: flex; align-items: center; gap: 8px;
					b { font-size: 14px; color: #1a1a2e; }
					.phone { font-size: 12px; color: #a0aec0; }
				}
				.fee-line { font-size: 12px; color: #8f9bb3; margin-top: 2px;
					.fee { color: #e6a23c; font-size: 13px; }
					.hospital { margin-left: 10px; color: #a0aec0; }
				}
			}

			.done-badge {
				font-size: 12px; color: #059669; font-weight: 600;
				background: rgba(5,150,105,0.06); padding: 4px 12px; border-radius: 6px;
			}
		}

		.file-checklist {
			display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 10px;

			.file-item {
				font-size: 12px; padding: 3px 10px; border-radius: 6px;
				background: #f0f0f5; color: #a0aec0;
				display: flex; align-items: center; gap: 3px;

				&.done { background: rgba(5,150,105,0.06); color: #059669; }
				&.required { background: rgba(230,162,60,0.08); color: #e6a23c; }

				.source-tag {
					font-size: 10px; color: #667eea; margin-left: 2px;
					background: rgba(102,126,234,0.08); padding: 1px 5px; border-radius: 3px;
				}
			}
		}

		.doctor-actions { display: flex; justify-content: flex-end; }
	}

	.empty-state {
		display: flex; flex-direction: column; align-items: center; padding: 60px 20px; color: #a8a29e;
		i { font-size: 40px; margin-bottom: 10px; }
		p { font-size: 14px; }
	}
}
</style>
